<template>
  <div class="export-polygon-clue-area-constructor main-body">
    <EditCom
      :target="exportTarget"
      :title="dialogTitle"
      :inputList="exportInputList"
      :rules="exportRules"
      :buttonList="exportButtonList"
      :showClose="false"
    />
  </div>
</template>

<script setup>
import {
  ref, reactive, computed, onMounted, watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { dayjs, message } from '@/utils/js/initVariable';
import EditCom from '@/components/EditCom';
import { queryScanSatelliteType } from '@/api/PolygonClueAreaApi';
import { queryCustomRegionList } from '@/api/Region';
import { exportClueArea } from '@/api/ExportData';

// ==================== 路由标题 ====================
const route = useRoute();
const dialogTitle = computed(() => route.meta?.title || '线索区导出');

// ==================== 默认值常量 ====================
const DEFAULT_DAY_LIMIT = 31;
const DEFAULT_ORIGINAL_AREA = true;
const DEFAULT_COMPUTED_AREA = false;
const DEFAULT_EXPORT_FORMATS = [3, 5];
const DEFAULT_MERGE_EXPORT = true;

// 区域类型选项
const areaTypeOptions = [
  { label: '原始区域', value: 'original' },
  { label: '计算区域', value: 'computed' },
];

// 文件类型选项
const exportFormatOptions = [
  { label: 'SHP', value: 3 },
  { label: 'Excel', value: 5 },
];

// 导出规则选项（单选）
const mergeExportOptions = [
  { label: '合并', value: true },
  { label: '拆分', value: false },
];

const getDefaultTarget = () => {
  const initialAreaTypes = [];
  if (DEFAULT_ORIGINAL_AREA) initialAreaTypes.push('original');
  if (DEFAULT_COMPUTED_AREA) initialAreaTypes.push('computed');
  return {
    satelliteTypes: [],
    satelliteTypeList: [],
    startTime: '',
    endTime: '',
    dayLimit: DEFAULT_DAY_LIMIT,
    regionIds: [],
    regionIdList: [],
    areaTypes: initialAreaTypes,
    areaTypeList: areaTypeOptions,
    exportFormats: DEFAULT_EXPORT_FORMATS,
    exportFormatList: exportFormatOptions,
    mergeExport: [DEFAULT_MERGE_EXPORT],
    mergeExportList: mergeExportOptions,
  };
};

// ==================== 表单数据对象 ====================
const exportTarget = reactive(getDefaultTarget());
const regionLoading = ref(true);
const satelliteTypeLoading = ref(true);

// ==================== 导出规则单选限制 ====================
watch(
  () => exportTarget.mergeExport,
  (newVal, oldVal) => {
    if (newVal.length > 1) {
      // 只保留最后一次选中的项（新值中不在旧值里的那一项）
      const added = newVal.filter(v => !oldVal.includes(v));
      exportTarget.mergeExport = added.length > 0 ? [added[added.length - 1]] : [newVal[newVal.length - 1]];
    }
    if (newVal.length === 0) {
      // 至少选一项，回退到上次的值
      exportTarget.mergeExport = oldVal.length > 0 ? [oldVal[oldVal.length - 1]] : [DEFAULT_MERGE_EXPORT];
    }
  },
);

// ==================== 时间相互限制 ====================
const todayEnd = () => dayjs().endOf('day');

const startTimeDisabledDate = (date) => {
  if (dayjs(date).isAfter(todayEnd())) return true;
  if (!exportTarget.endTime) return false;
  const end = dayjs(exportTarget.endTime).startOf('day');
  const minDate = end.subtract(exportTarget.dayLimit - 1, 'day');
  return dayjs(date).isAfter(end) || dayjs(date).isBefore(minDate);
};

const endTimeDisabledDate = (date) => {
  if (dayjs(date).isAfter(todayEnd())) return true;
  if (!exportTarget.startTime) return false;
  const start = dayjs(exportTarget.startTime).startOf('day');
  const maxDate = start.add(exportTarget.dayLimit - 1, 'day');
  return dayjs(date).isBefore(start) || dayjs(date).isAfter(maxDate);
};

// ==================== 表单字段配置 ====================
const exportInputList = computed(() => [
  {
    name: '卫星类型',
    prop: 'satelliteTypes',
    type: 'select',
    select: {
      list: 'satelliteTypeList',
      label: 'label',
      value: 'value',
      multiple: true,
      filterable: true,
      loading: satelliteTypeLoading.value,
    },
  },
  {
    name: '开始时间',
    prop: 'startTime',
    type: 'date',
    date: {
      disabledFun: startTimeDisabledDate,
    },
  },
  {
    name: '结束时间',
    prop: 'endTime',
    type: 'date',
    date: {
      disabledFun: endTimeDisabledDate,
    },
  },
  {
    name: '导出范围',
    prop: 'regionIds',
    type: 'select',
    select: {
      list: 'regionIdList',
      label: 'polygonName',
      value: 'id',
      multiple: true,
      filterable: true,
      loading: regionLoading.value,
    },
  },
  {
    name: '区域类型',
    prop: 'areaTypes',
    type: 'checkboxgroup',
    checkboxgroup: {
      list: 'areaTypeList',
      label: 'label',
      value: 'value',
      checkboxButton: true,
    },
  },
  {
    name: '文件类型',
    prop: 'exportFormats',
    type: 'checkboxgroup',
    checkboxgroup: {
      list: 'exportFormatList',
      label: 'label',
      value: 'value',
      checkboxButton: true,
    },
  },
  {
    name: '导出规则',
    prop: 'mergeExport',
    type: 'checkboxgroup',
    checkboxgroup: {
      list: 'mergeExportList',
      label: 'label',
      value: 'value',
      checkboxButton: true,
    },
  },
]);

// ==================== 表单校验规则 ====================
const validateEndTime = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请选择结束时间'));
    return;
  }
  if (exportTarget.startTime) {
    const start = dayjs(exportTarget.startTime);
    const end = dayjs(value);
    if (end.isBefore(start)) {
      callback(new Error('结束时间不能早于开始时间'));
      return;
    }
    if (end.diff(start, 'day') >= exportTarget.dayLimit) {
      callback(new Error(`结束时间与开始时间相差不能超过${exportTarget.dayLimit}天`));
      return;
    }
  }
  callback();
};

const validateStartTime = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请选择开始时间'));
    return;
  }
  callback();
};

const validateAreaTypes = (rule, value, callback) => {
  if (!value || value.length === 0) {
    callback(new Error('请至少选择一种区域类型'));
    return;
  }
  callback();
};

const exportRules = reactive({
  startTime: [
    { required: true, validator: validateStartTime, trigger: 'change' },
  ],
  endTime: [
    { required: true, validator: validateEndTime, trigger: 'change' },
  ],
  areaTypes: [
    { required: true, validator: validateAreaTypes, trigger: 'change' },
  ],
  exportFormats: [
    { required: true, validator: validateAreaTypes, trigger: 'change' },
  ],
});


// ==================== 底部按钮 ====================
const handleExport = (formRef, target, button, loadFn) => {
  formRef.value.validate((valid) => {
    if (valid) {
      const params = {
        satelliteTypes: target.satelliteTypes,
        startTime: target.startTime ? dayjs(target.startTime).format('YYYY-MM-DD 00:00:00') : '',
        endTime: target.endTime ? dayjs(target.endTime).format('YYYY-MM-DD 23:59:59') : '',
        regionIds: target.regionIds,
        originalArea: target.areaTypes.includes('original'),
        computedArea: target.areaTypes.includes('computed'),
        exportFormats: target.exportFormats,
        mergeExport: target.mergeExport[0],
      };
      console.log('导出参数:', params);
      exportClueArea(params).then(res => {
        if (res.data?.code === 1004) {
          message('导出成功，请前往下载任务中心查看', 1);
        }
      }).catch(err => {
        console.error('导出失败:', err);
      });
    }
    loadFn();
  });
};

const handleReset = (formRef, target, button, loadFn) => {
  Object.assign(target, getDefaultTarget());
  formRef.value?.resetFields();
  loadFn();
};

const exportButtonList = ref([
  { name: '导出', type: 'primary', fun: handleExport },
  { name: '重置', type: 'default', fun: handleReset },
]);

// ==================== API 获取选项列表 ====================
const fetchOptions = async () => {
  try {
    // 获取卫星类型列表
    satelliteTypeLoading.value = true;
    const satelliteRes = await queryScanSatelliteType({});
    if (satelliteRes.data?.dataList) {
      exportTarget.satelliteTypeList = satelliteRes.data.dataList.map(item => ({
        label: item,
        value: item,
      }));
    }
  } catch (e) {
    console.error('获取卫星类型失败:', e);
  } finally {
    satelliteTypeLoading.value = false;
  }

  try {
    // 获取导出范围列表
    regionLoading.value = true;
    const regionRes = await queryCustomRegionList(null);
    if (regionRes.data?.dataList) {
      exportTarget.regionIdList = regionRes.data.dataList;
    }
  } catch (e) {
    console.error('获取导出范围失败:', e);
  } finally {
    regionLoading.value = false;
  }
};

onMounted(() => {
  fetchOptions();
});
</script>

<style lang="less" scoped>
.export-polygon-clue-area-constructor {
  padding: 0;
  height: 100%;
}
</style>
